import { ValidPhoneCode } from './format-phone';

/** Длина национальной части номера — без кода страны. */
const nationalLengthByCode: Record<ValidPhoneCode, number> = {
  '+7': 10,
  '+375': 9,
  '+374': 8,
};

/**
 * Цифры кода страны, с которых номер может начинаться. У России и Казахстана их два:
 * международный `7` и междугородний `8` — оба означают одно и то же.
 */
const countryCodeDigitsByCode: Record<ValidPhoneCode, string[]> = {
  '+7': ['7', '8'],
  '+375': ['375'],
  '+374': ['374'],
};

/**
 * Снимает код страны с начала строки цифр, если он там есть.
 *
 * Признак «код страны присутствует» — длина строки больше национальной: иначе `9885634158`
 * (номер без кода) и `89885634158` (он же с кодом) не отличить, и у первого отрезалась бы
 * значащая цифра.
 */
export const stripCountryCode = (digits: string, code: ValidPhoneCode): string => {
  const nationalLength = nationalLengthByCode[code];

  if (!nationalLength || digits.length <= nationalLength) {
    return digits;
  }

  const countryCode = countryCodeDigitsByCode[code].find((prefix) => digits.startsWith(prefix));

  return countryCode ? digits.slice(countryCode.length) : digits;
};

export const parsePhone = (value = '', code?: ValidPhoneCode): string => {
  const normalizedPhone = value.replace(/[^0-9+]/g, '');

  let codeToUse = code;

  if (!code) {
    const offset = normalizedPhone.startsWith('+') ? 1 : 0;
    if (normalizedPhone.startsWith('7', offset)) {
      codeToUse = '+7';
    } else if (normalizedPhone.startsWith('375', offset)) {
      codeToUse = '+375';
    } else if (normalizedPhone.startsWith('374', offset)) {
      codeToUse = '+374';
    }
  }

  const maxLength = codeToUse ? nationalLengthByCode[codeToUse] : undefined;

  if (!maxLength) {
    return normalizedPhone;
  }

  const digits = normalizedPhone.replace(/[^0-9]/g, '');

  // Код страны снимается до обрезки: иначе у номера, вставленного целиком,
  // отрезается последняя значащая цифра, а префикс приклеивается заново.
  return stripCountryCode(digits, codeToUse as ValidPhoneCode).slice(0, maxLength);
};
